/**
 * The scoring engine.
 *
 * One pure function: apply(state, command, ruleset) -> state. It reads no
 * clock, touches no network, and throws RuleViolation rather than guessing when
 * a scorer taps something the laws do not allow.
 *
 * Every rule it enforces comes from the ruleset, not from this file. That is
 * what makes the tournament panel meaningful: change a number there and the
 * engine behaves differently, with no code change.
 */
import { RuleViolation, } from "./types.js";
/* ── dismissal legality, from the laws rather than the ruleset ───────────── */
/** Off a no-ball or a free hit the ball is effectively dead for most kinds. */
const DEAD_BALL_DISMISSALS = ["RUN_OUT", "OBSTRUCTING_FIELD", "HIT_BALL_TWICE"];
/** Off a wide the batter cannot have hit it, so bat-related kinds are out. */
const WIDE_DISMISSALS = ["RUN_OUT", "STUMPED", "HIT_WICKET", "OBSTRUCTING_FIELD"];
/** Kinds the bowler is credited with. */
const BOWLER_CREDITED = ["BOWLED", "CAUGHT", "LBW", "STUMPED", "HIT_WICKET"];
/* ── helpers ────────────────────────────────────────────────────────────── */
const otherEnd = (e) => (e === "END_A" ? "END_B" : "END_A");
export function strikerEnd(inn) {
    return otherEnd(inn.bowlerEnd);
}
export function strikerOf(inn) {
    return inn.atEnd[strikerEnd(inn)];
}
export function nonStrikerOf(inn) {
    return inn.atEnd[inn.bowlerEnd];
}
function newBatter(playerId) {
    return { playerId, runs: 0, ballsFaced: 0, fours: 0, sixes: 0, bonusRuns: 0, out: false, dismissals: 0 };
}
function newBowler(playerId) {
    return { playerId, legalBalls: 0, runsConceded: 0, wickets: 0, maidens: 0, runsThisOver: 0 };
}
export function oversOf(balls, perOver) {
    return `${Math.floor(balls / perOver)}.${balls % perOver}`;
}
function current(state) {
    const inn = state.innings[state.currentInnings];
    if (!inn)
        throw new RuleViolation("NO_INNINGS", "No innings has been started.");
    return inn;
}
/** Structural clone. The engine never mutates what it was given. */
function cloneInnings(i) {
    return {
        ...i,
        extras: { ...i.extras },
        atEnd: { ...i.atEnd },
        battingOrder: [...i.battingOrder],
        batters: Object.fromEntries(Object.entries(i.batters).map(([k, v]) => [k, { ...v }])),
        bowlers: Object.fromEntries(Object.entries(i.bowlers).map(([k, v]) => [k, { ...v }])),
        fallOfWickets: [...i.fallOfWickets],
    };
}
function withInnings(state, inn) {
    const innings = [...state.innings];
    innings[state.currentInnings] = inn;
    return { ...state, innings };
}
/* ── the empty match ────────────────────────────────────────────────────── */
export function emptyMatch(ruleset) {
    return {
        rulesetId: ruleset.id,
        innings: [],
        currentInnings: -1,
        complete: false,
        target: null,
    };
}
/* ── validation of one delivery ─────────────────────────────────────────── */
function validateDelivery(d, inn, rs) {
    if (d.wide && d.noBall) {
        throw new RuleViolation("WIDE_AND_NO_BALL", "A ball is a wide or a no-ball, never both.");
    }
    if (d.wide && d.runsOffBat > 0) {
        throw new RuleViolation("RUNS_OFF_WIDE", "The bat cannot score off a wide.");
    }
    if (d.byes > 0 && d.legByes > 0) {
        throw new RuleViolation("BYES_AND_LEGBYES", "A delivery yields byes or leg byes, not both.");
    }
    if (d.batBoundary === "FOUR" && d.runsOffBat !== 4) {
        throw new RuleViolation("BOUNDARY_MISMATCH", "A four must credit four runs off the bat.");
    }
    if (d.batBoundary === "SIX" && d.runsOffBat !== 6) {
        throw new RuleViolation("BOUNDARY_MISMATCH", "A six must credit six runs off the bat.");
    }
    if (d.batBoundary !== null && d.runsRun > 0) {
        throw new RuleViolation("BOUNDARY_WITH_RUNNING", "Runs are not also run for a boundary.");
    }
    if (d.bonusZoneId) {
        const zone = rs.bonusZones.find((z) => z.id === d.bonusZoneId);
        if (!zone) {
            throw new RuleViolation("UNKNOWN_BONUS_ZONE", `This tournament has no bonus zone called "${d.bonusZoneId}".`);
        }
    }
    const striker = strikerOf(inn);
    const nonStriker = nonStrikerOf(inn);
    if (striker && d.strikerId !== striker) {
        throw new RuleViolation("BATTER_DESYNC", "The app and the scorer disagree about who is on strike. Reload before recording.");
    }
    if (nonStriker && d.nonStrikerId !== nonStriker) {
        throw new RuleViolation("BATTER_DESYNC", "The app and the scorer disagree about who is at the other end.");
    }
    if (inn.bowlerId && d.bowlerId !== inn.bowlerId) {
        throw new RuleViolation("BOWLER_DESYNC", "That is not the bowler on record for this over.");
    }
    if (!d.wicket)
        return;
    const w = d.wicket;
    if (!rs.dismissals.allowed.includes(w.kind)) {
        throw new RuleViolation("DISMISSAL_NOT_ALLOWED", `${w.kind.replace(/_/g, " ").toLowerCase()} is not used in this tournament.`);
    }
    const onFreeHit = inn.freeHit;
    if (d.noBall || onFreeHit) {
        if (!DEAD_BALL_DISMISSALS.includes(w.kind)) {
            throw new RuleViolation("ILLEGAL_DISMISSAL", `You cannot be ${w.kind.replace(/_/g, " ").toLowerCase()} off a ${onFreeHit ? "free hit" : "no-ball"}.`);
        }
    }
    else if (d.wide) {
        if (!WIDE_DISMISSALS.includes(w.kind)) {
            throw new RuleViolation("ILLEGAL_DISMISSAL", `You cannot be ${w.kind.replace(/_/g, " ").toLowerCase()} off a wide.`);
        }
    }
    if (w.kind === "CAUGHT" && (d.runsRun > 0 || d.byes > 0 || d.legByes > 0)) {
        throw new RuleViolation("RUNS_ON_CATCH", "No runs are scored when a catch is taken.");
    }
    if (w.dismissedBatterId !== striker && w.dismissedBatterId !== nonStriker) {
        throw new RuleViolation("DISMISSED_NOT_AT_CREASE", "The dismissed batter is not at the crease.");
    }
    // In double wicket a batter can be dismissed repeatedly — each one costs the
    // pair runs rather than removing anyone. `out` only blocks where they leave.
    const bs = inn.batters[w.dismissedBatterId];
    if (bs?.out && !rs.dismissals.batterContinuesAfterOut) {
        throw new RuleViolation("BATTER_ALREADY_OUT", "That batter is already out.");
    }
}
/* ── arithmetic for one delivery ────────────────────────────────────────── */
function derive(d, inn, rs) {
    const legal = !d.wide && !d.noBall;
    const zone = d.bonusZoneId
        ? rs.bonusZones.find((z) => z.id === d.bonusZoneId)
        : undefined;
    const bonusRuns = zone ? zone.bonus : 0;
    const extraPenalty = d.wide ? rs.extras.widePenalty : d.noBall ? rs.extras.noBallPenalty : 0;
    const totalRuns = d.runsOffBat + d.byes + d.legByes + d.runsRun + extraPenalty + d.penaltyRuns + bonusRuns;
    // What the bowler wears. A league can choose whether wide runs and no-ball
    // byes are charged to them.
    let runsChargedToBowler = d.runsOffBat + extraPenalty + bonusRuns;
    if (d.wide && rs.extras.wideRunsChargedToBowler)
        runsChargedToBowler += d.runsRun;
    if (d.noBall) {
        runsChargedToBowler += d.runsRun;
        if (rs.extras.noBallByesChargedToBowler)
            runsChargedToBowler += d.byes + d.legByes;
    }
    const batterFacedBall = (legal) ||
        (d.noBall && rs.batting.ballsFacedIncludesNoBalls) ||
        (d.wide && rs.batting.ballsFacedIncludesWides);
    // In double wicket a wicket never ends anything; it deducts runs instead.
    const wicketCounts = !!d.wicket && !rs.dismissals.batterContinuesAfterOut;
    const penaltyApplied = d.wicket ? rs.dismissals.penalty : 0;
    const bowlerCredited = !!d.wicket && BOWLER_CREDITED.includes(d.wicket.kind);
    const countsTowardOver = legal || rs.extras.extrasCountAsBall;
    const overCompleted = countsTowardOver && inn.legalBallsThisOver + 1 >= rs.ballsPerOver;
    const freeHitNext = d.noBall && rs.extras.freeHitAfterNoBall;
    return {
        legal, totalRuns, runsChargedToBowler, bonusRuns, batterFacedBall,
        wicketCounts, bowlerCredited, overCompleted, freeHitNext, penaltyApplied,
    };
}
/* ── innings completion ─────────────────────────────────────────────────── */
function checkComplete(inn, rs, target) {
    if (inn.complete)
        return inn;
    if (rs.completion.targetReached && target !== null && inn.totalRuns >= target) {
        return { ...inn, complete: true, endedBecause: "TARGET" };
    }
    if (rs.completion.allOut && inn.wickets >= rs.maxWickets) {
        return { ...inn, complete: true, endedBecause: "ALL_OUT" };
    }
    if (rs.completion.oversComplete && inn.legalBalls >= rs.oversPerInnings * rs.ballsPerOver) {
        return { ...inn, complete: true, endedBecause: "OVERS" };
    }
    return inn;
}
/* ── the reducer ────────────────────────────────────────────────────────── */
export function apply(state, command, rs) {
    if (state.complete && command.type !== "VOID_DELIVERY") {
        throw new RuleViolation("MATCH_COMPLETE", "This match is over.");
    }
    switch (command.type) {
        case "START_INNINGS": return startInnings(state, command);
        case "SELECT_BATTER": return selectBatter(state, command.playerId, rs);
        case "SELECT_BOWLER": return selectBowler(state, command.playerId, rs);
        case "RECORD_DELIVERY": return recordDelivery(state, command.delivery, rs);
        case "END_INNINGS": return endInnings(state, command.reason ?? "DECLARED", rs);
        case "VOID_DELIVERY":
            throw new RuleViolation("NO_SUCH_DELIVERY", "Voiding is handled by replaying the ledger, not by the reducer.");
        default: {
            // A command the engine does not know — a typo, or a ledger written by a
            // newer version. Falling through returned undefined, and the NEXT command
            // then read .innings on it. Silent corruption; throw instead.
            const unknown = command;
            throw new RuleViolation("UNKNOWN_COMMAND", `"${unknown.type ?? "?"}" is not something the engine knows how to do.`);
        }
    }
}
function startInnings(state, c) {
    const index = state.innings.length;
    const [first, second] = c.battingOrder;
    if (!first)
        throw new RuleViolation("NO_BATTERS_LEFT", "The batting order is empty.");
    const batters = { [first]: newBatter(first) };
    const atEnd = { END_A: null, END_B: null };
    // The bowler starts at END_A, so the striker stands at END_B.
    atEnd.END_B = first;
    let nextBatterIndex = 1;
    if (second) {
        batters[second] = newBatter(second);
        atEnd.END_A = second;
        nextBatterIndex = 2;
    }
    const inn = {
        index,
        battingTeamId: c.battingTeamId,
        bowlingTeamId: c.bowlingTeamId,
        totalRuns: 0, wickets: 0, legalBalls: 0, legalBallsThisOver: 0, overIndex: 0,
        penaltyRuns: 0, bonusRuns: 0,
        extras: { wides: 0, noBalls: 0, byes: 0, legByes: 0, penalty: 0 },
        atEnd, bowlerEnd: "END_A", bowlerId: null, previousOverBowlerId: null,
        awaitingBatter: !second, awaitingBowler: true, freeHit: false,
        battingOrder: [...c.battingOrder], nextBatterIndex,
        batters, bowlers: {}, fallOfWickets: [],
        complete: false,
    };
    // Chasing: the target is one more than the first innings total.
    const target = index > 0 && state.innings[0]
        ? state.innings[0].totalRuns + 1
        : null;
    return { ...state, innings: [...state.innings, inn], currentInnings: index, target };
}
function selectBatter(state, playerId, rs) {
    const inn = cloneInnings(current(state));
    if (inn.complete)
        throw new RuleViolation("INNINGS_COMPLETE", "This innings is over.");
    if (!inn.awaitingBatter) {
        throw new RuleViolation("NOT_AWAITING_BATTER", "Nobody is waiting to come in.");
    }
    const existing = inn.batters[playerId];
    if (existing?.out && !rs.dismissals.retiredBattersMayReturn) {
        throw new RuleViolation("BATTER_ALREADY_OUT", "That batter is already out.");
    }
    if (inn.atEnd.END_A === playerId || inn.atEnd.END_B === playerId) {
        throw new RuleViolation("BATTER_NOT_AVAILABLE", "That batter is already at the crease.");
    }
    const vacant = inn.atEnd.END_A === null ? "END_A" : "END_B";
    if (inn.atEnd[vacant] !== null) {
        throw new RuleViolation("BATTER_NOT_AVAILABLE", "Both ends are occupied.");
    }
    inn.atEnd[vacant] = playerId;
    inn.batters[playerId] = existing && !existing.out ? existing : newBatter(playerId);
    inn.awaitingBatter = false;
    if (!inn.battingOrder.includes(playerId))
        inn.battingOrder.push(playerId);
    return withInnings(state, inn);
}
function selectBowler(state, playerId, rs) {
    const inn = cloneInnings(current(state));
    if (inn.complete)
        throw new RuleViolation("INNINGS_COMPLETE", "This innings is over.");
    if (!inn.awaitingBowler) {
        throw new RuleViolation("NOT_AWAITING_BOWLER", "An over is already in progress.");
    }
    if (!rs.bowler.allowConsecutiveOvers && inn.previousOverBowlerId === playerId) {
        throw new RuleViolation("CONSECUTIVE_OVERS", "A bowler may not bowl consecutive overs.");
    }
    const b = inn.bowlers[playerId];
    const bowled = b ? Math.floor(b.legalBalls / rs.ballsPerOver) : 0;
    const q = rs.bowler.quota;
    const cap = q.kind === "FIXED" ? q.maxOvers
        : q.kind === "FRACTION" ? Math.ceil(rs.oversPerInnings / q.divisor)
            : Infinity;
    if (bowled >= cap) {
        throw new RuleViolation("BOWLER_QUOTA_EXCEEDED", `They have bowled their ${cap} over${cap === 1 ? "" : "s"}.`);
    }
    inn.bowlerId = playerId;
    inn.bowlers[playerId] = b ?? newBowler(playerId);
    inn.bowlers[playerId].runsThisOver = 0;
    inn.awaitingBowler = false;
    return withInnings(state, inn);
}
function recordDelivery(state, d, rs) {
    const before = current(state);
    if (before.complete)
        throw new RuleViolation("INNINGS_COMPLETE", "This innings is over.");
    if (before.awaitingBatter) {
        throw new RuleViolation("AWAITING_BATTER", "A batter must come in before the next ball.");
    }
    if (before.awaitingBowler) {
        throw new RuleViolation("AWAITING_BOWLER", "Choose a bowler before the next ball.");
    }
    validateDelivery(d, before, rs);
    const dv = derive(d, before, rs);
    const inn = cloneInnings(before);
    /* runs */
    inn.totalRuns += dv.totalRuns;
    inn.bonusRuns += dv.bonusRuns;
    if (d.wide)
        inn.extras.wides += rs.extras.widePenalty + d.runsRun;
    if (d.noBall)
        inn.extras.noBalls += rs.extras.noBallPenalty;
    inn.extras.byes += d.byes;
    inn.extras.legByes += d.legByes;
    inn.extras.penalty += d.penaltyRuns;
    /* the striker */
    const striker = inn.batters[d.strikerId] ?? newBatter(d.strikerId);
    striker.runs += d.runsOffBat;
    striker.bonusRuns += dv.bonusRuns;
    if (dv.bonusRuns)
        striker.runs += dv.bonusRuns;
    if (dv.batterFacedBall)
        striker.ballsFaced += 1;
    if (d.batBoundary === "FOUR")
        striker.fours += 1;
    if (d.batBoundary === "SIX")
        striker.sixes += 1;
    const zone = d.bonusZoneId ? rs.bonusZones.find((z) => z.id === d.bonusZoneId) : undefined;
    if (zone?.countsAsSix)
        striker.sixes += 1;
    inn.batters[d.strikerId] = striker;
    /* the bowler */
    const bowler = inn.bowlers[d.bowlerId] ?? newBowler(d.bowlerId);
    if (dv.legal || rs.extras.extrasCountAsBall)
        bowler.legalBalls += 1;
    bowler.runsConceded += dv.runsChargedToBowler;
    bowler.runsThisOver += dv.runsChargedToBowler;
    if (dv.bowlerCredited)
        bowler.wickets += 1;
    inn.bowlers[d.bowlerId] = bowler;
    /* the wicket */
    if (d.wicket) {
        const victim = inn.batters[d.wicket.dismissedBatterId] ?? newBatter(d.wicket.dismissedBatterId);
        victim.dismissals += 1;
        victim.dismissal = d.wicket;
        // They only leave the crease where the format says a wicket removes them.
        if (dv.wicketCounts)
            victim.out = true;
        inn.batters[d.wicket.dismissedBatterId] = victim;
        // The penalty is applied whether or not the wicket "counts", because in
        // double wicket the whole point is that it costs runs instead.
        if (dv.penaltyApplied !== 0) {
            inn.totalRuns += dv.penaltyApplied;
            inn.penaltyRuns += dv.penaltyApplied;
        }
        if (dv.wicketCounts) {
            inn.wickets += 1;
            inn.fallOfWickets.push({
                playerId: d.wicket.dismissedBatterId,
                runs: inn.totalRuns,
                wickets: inn.wickets,
                over: oversOf(inn.legalBalls + (dv.legal ? 1 : 0), rs.ballsPerOver),
                sequence: inn.fallOfWickets.length + 1,
            });
            // The dismissed batter leaves; that end is vacant.
            const endOf = inn.atEnd.END_A === d.wicket.dismissedBatterId ? "END_A"
                : inn.atEnd.END_B === d.wicket.dismissedBatterId ? "END_B"
                    : null;
            if (endOf)
                inn.atEnd[endOf] = null;
            inn.awaitingBatter = true;
        }
        // In double wicket the batter stays exactly where they are.
    }
    /* balls and strike */
    if (dv.legal) {
        inn.legalBalls += 1;
        inn.legalBallsThisOver += 1;
    }
    else if (rs.extras.extrasCountAsBall) {
        inn.legalBallsThisOver += 1;
    }
    // Odd runs swap the ends. Byes and leg byes are run, so they rotate too.
    const ran = d.runsOffBat + d.byes + d.legByes + d.runsRun;
    const rotates = d.batBoundary === null && ran % 2 === 1;
    if (rotates || (d.battersCrossed && d.wicket)) {
        const a = inn.atEnd.END_A;
        inn.atEnd.END_A = inn.atEnd.END_B;
        inn.atEnd.END_B = a;
    }
    /* the over */
    if (dv.overCompleted) {
        if (bowler.runsThisOver === 0 && !d.wicket)
            bowler.maidens += 1;
        inn.overIndex += 1;
        inn.legalBallsThisOver = 0;
        inn.previousOverBowlerId = inn.bowlerId;
        inn.bowlerId = null;
        inn.awaitingBowler = true;
        inn.bowlerEnd = otherEnd(inn.bowlerEnd); // the bowling end changes
    }
    inn.freeHit = dv.freeHitNext;
    const checked = checkComplete(inn, rs, state.target);
    let next = withInnings(state, checked);
    if (checked.complete)
        next = afterInnings(next, rs);
    return next;
}
function endInnings(state, reason, rs) {
    const inn = cloneInnings(current(state));
    if (inn.complete)
        throw new RuleViolation("INNINGS_COMPLETE", "This innings is already over.");
    inn.complete = true;
    inn.endedBecause = reason;
    return afterInnings(withInnings(state, inn), rs);
}
/** Decide whether the match is over, and work out the result if it is. */
function afterInnings(state, rs) {
    const played = state.innings.filter((i) => i.complete).length;
    const expected = rs.inningsPerTeam * 2;
    if (played < expected)
        return state;
    const [a, b] = state.innings;
    if (!a || !b)
        return state;
    const result = a.totalRuns > b.totalRuns
        ? {
            winnerTeamId: a.battingTeamId, method: "RUNS",
            margin: a.totalRuns - b.totalRuns,
            summary: `won by ${a.totalRuns - b.totalRuns} run${a.totalRuns - b.totalRuns === 1 ? "" : "s"}`,
        }
        : b.totalRuns > a.totalRuns
            ? {
                winnerTeamId: b.battingTeamId, method: "WICKETS",
                margin: Math.max(rs.maxWickets - b.wickets, 0),
                summary: `won by ${Math.max(rs.maxWickets - b.wickets, 0)} wicket${Math.max(rs.maxWickets - b.wickets, 0) === 1 ? "" : "s"}`,
            }
            : { winnerTeamId: null, method: "TIE", margin: 0, summary: "match tied" };
    return { ...state, complete: true, result };
}
/**
 * The single value the scorer UI switches on. When this says AWAITING_BATTER
 * there is no keypad in that branch — not hidden, absent — so a run cannot be
 * recorded while nobody is at the crease.
 *
 * `squads` maps teamId to that side's players. Without it the bowler pool falls
 * back to whoever has already bowled, which at the start of an innings is
 * nobody — the caller must pass it.
 */
export function phaseOf(state, rs, squads = {}) {
    if (state.complete)
        return { kind: "COMPLETE" };
    const inn = state.innings[state.currentInnings];
    if (!inn)
        return { kind: "NO_INNINGS" };
    if (inn.complete) {
        if (state.innings.length >= rs.inningsPerTeam * 2)
            return { kind: "COMPLETE" };
        // The sides swap. Naming them here means the UI does not have to work it out.
        return {
            kind: "INNINGS_BREAK",
            next: state.innings.length,
            battingTeamId: inn.bowlingTeamId,
            bowlingTeamId: inn.battingTeamId,
        };
    }
    if (inn.awaitingBatter) {
        const atCrease = new Set([inn.atEnd.END_A, inn.atEnd.END_B].filter(Boolean));
        // Anyone in the side, not only those named in the original order — a player
        // left out of the order should still be able to come in.
        const pool = squads[inn.battingTeamId]?.length
            ? Array.from(new Set([...inn.battingOrder, ...squads[inn.battingTeamId]]))
            : inn.battingOrder;
        const available = pool.filter((p) => !atCrease.has(p) && !(inn.batters[p]?.out ?? false));
        return { kind: "AWAITING_BATTER", available };
    }
    if (inn.awaitingBowler) {
        const q = rs.bowler.quota;
        const cap = q.kind === "FIXED" ? q.maxOvers
            : q.kind === "FRACTION" ? Math.ceil(rs.oversPerInnings / q.divisor)
                : Infinity;
        const pool = squads[inn.bowlingTeamId]?.length
            ? squads[inn.bowlingTeamId]
            : Object.keys(inn.bowlers);
        const blocked = pool.filter((p) => {
            if (!rs.bowler.allowConsecutiveOvers && inn.previousOverBowlerId === p)
                return true;
            const b = inn.bowlers[p];
            return b ? Math.floor(b.legalBalls / rs.ballsPerOver) >= cap : false;
        });
        return { kind: "AWAITING_BOWLER", available: pool.filter((p) => !blocked.includes(p)), blocked };
    }
    const striker = strikerOf(inn);
    const nonStriker = nonStrikerOf(inn);
    if (!striker || !nonStriker || !inn.bowlerId)
        return { kind: "AWAITING_BATTER", available: [] };
    return {
        kind: "READY",
        striker, nonStriker, bowler: inn.bowlerId, freeHit: inn.freeHit,
    };
}
//# sourceMappingURL=engine.js.map